<template>
  <div>
    <div
      v-if="isVibood"
      class="flex flex-col items-center justify-center p-10 text-center space-y-4"
    >
      <p class="text-lg text-gray-700">
        El envío de evaluaciones se hace ahora desde Psypilot
      </p>
      <a
        href="https://app.psypilot.com"
        target="_blank"
        rel="noopener noreferrer"
        class="px-6 py-2 bg-secondary text-white rounded-xl shadow-lg hover:bg-opacity-90 transition font-medium"
      >
        Ir a Psypilot
      </a>
    </div>
    <div v-else class="flex flex-col items-center justify-center p-6">
      <div class="w-full max-w-2xl space-y-6">
        <div class="flex gap-4">
          <input
            v-model="clientId"
            type="text"
            :placeholder="$t('client-id-placeholder')"
            class="flex-1 px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
          />
          <button
            @click="generateLink"
            class="px-6 py-2 bg-primary text-black rounded-xl shadow-grey shadow-lg hover:bg-opacity-90 hover:shadow-md transform transition-all duration-200 ease-in-out font-medium"
          >
            {{ $t('generate') }}
          </button>
        </div>
        <div class="flex items-center gap-4">
          <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
            {{ $t('select-language') }}
          </label>
          <select
            v-model="selectedLanguage"
            class="flex-1 px-4 py-2 border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
          >
            <option value="es">{{ $t('lang-es') }}</option>
            <option value="en">{{ $t('lang-en') }}</option>
            <option value="ca">{{ $t('lang-ca') }}</option>
            <option v-if="isDev" value="fr">{{ $t('lang-fr') }}</option>
            <option v-if="isDev" value="it">{{ $t('lang-it') }}</option>
            <option v-if="isDev" value="pt">{{ $t('lang-pt') }}</option>
          </select>
        </div>
        <div v-if="generatedLink" class="space-y-4">
          <p class="text-lg text-gray-600">
            {{ $t('link-generated') }}
          </p>
          <div class="flex gap-4">
            <input
              type="text"
              :value="generatedLink"
              readonly
              ref="generatedLinkInput"
              class="flex-1 px-4 py-2 text-center border border-gray-300 text-black bg-gray-50 rounded-xl shadow-grey shadow-lg"
            />
            <button
              class="px-6 py-2 bg-primary text-black rounded-xl shadow-grey shadow-lg hover:bg-opacity-90 hover:shadow-md transform transition-all duration-200 ease-in-out font-medium"
              @click="copyLink"
              :title="$t('copy-link')"
              :class="{ 'bg-secondary text-white': isCopied }"
            >
              <i class="fas" :class="isCopied ? 'fa-check' : 'fa-copy'"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isDefaultTheme"
      class="w-full max-w-3xl mt-10 mx-auto flex flex-col items-center"
    >
      <h3 class="text-lg font-semibold mb-4 text-center">
        {{ $t('generated-links-list') || 'Generated Links' }}
      </h3>
      <div
        v-for="link in links"
        :key="link.clientId"
        class="flex flex-col md:flex-row items-center gap-3 mb-4 p-4 border border-gray-200 rounded-2xl shadow-lg bg-white dark:bg-gray-800 w-full max-w-3xl mx-auto transition hover:shadow-xl"
      >
        <span
          class="flex-1 truncate text-gray-700 dark:text-gray-200 text-center md:text-left"
        >
          <span class="font-semibold">ID:</span> {{ link.clientId }}
          <template v-if="link.professionalId && link.professionalId !== ''">
            <br />
            <span class="font-semibold">{{ $t('professional-id') }}:</span>
            {{ link.professionalId }}
          </template>
          <br />
          <span class="font-semibold">{{ $t('generation-date') }}:</span>
          {{ formatGenerationDate(link.generationDate) }}
        </span>
        <div class="flex gap-2 mt-2 md:mt-0">
          <template v-if="link.status === 'inactive'">
            <span
              class="flex items-center px-3 py-1 bg-gray-300 text-gray-700 text-xs font-semibold rounded-full mr-2 align-middle select-none"
            >
              {{ $t('inactive') }}
            </span>
            <button
              @click="activateLink(link)"
              class="px-4 py-2 bg-primary text-black rounded-xl font-medium shadow transition"
            >
              {{ $t('activate') }}
            </button>
          </template>
          <template v-else>
            <button
              @click="obtainLinkFromList(link)"
              class="px-4 py-2 rounded-xl font-medium shadow transition bg-primary text-black"
            >
              {{ $t('obtain-link') }}
            </button>
            <button
              @click="deactivateLinkHandler(link)"
              class="px-4 py-2 bg-secondary text-white rounded-xl font-medium shadow transition"
            >
              {{ $t('deactivate') }}
            </button>
          </template>
        </div>
      </div>
      <div
        v-if="loadingLinks"
        class="text-center py-4 text-gray-500 dark:text-gray-300"
      >
        {{ $t('loading') || 'Loading...' }}
      </div>
      <div
        v-if="allLinksLoaded && links.length === 0"
        class="text-center py-4 text-gray-500 dark:text-gray-300"
      >
        {{ $t('no-links-found') || 'No se han encontrado enlaces creados.' }}
      </div>
    </div>
  </div>
</template>

<script>
import '@fortawesome/fontawesome-free/css/all.css';
import { trackEvent } from '../utils/metrics';
import {
  generateActivationLink,
  getGeneratedLink,
  getLinks,
  deactivateLink,
} from '../services/medea-api';

export default {
  name: 'LinkGenerator',
  data() {
    return {
      clientId: '',
      selectedLanguage: 'es',
      generatedLink: '',
      isCopied: false,
      links: [],
      offset: 0,
      limit: 50,
      loadingLinks: false,
      allLinksLoaded: false,
      isDefaultTheme: false,
      isVibood: false,
    };
  },
  computed: {
    isDev() {
      return this.$route.query.env === 'dev';
    },
  },
  mounted() {
    const theme = document.documentElement.getAttribute('data-theme');
    this.isDefaultTheme = theme === 'default';
    this.isVibood = theme === 'VIBOOD';
    if (this.isDefaultTheme) {
      this.loadLinks();
      window.addEventListener('scroll', this.handleScroll);
    }
  },
  watch: {
    generatedLink(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const input = this.$refs.generatedLinkInput;
          if (input && input.scrollIntoView) {
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    formatGenerationDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      // Format: YYYY-MM-DD HH:mm
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    async generateLink() {
      // Trim whitespace
      this.clientId = this.clientId.trim();

      // Check if empty
      if (!this.clientId) {
        alert(this.$t('client-id-required'));
        return;
      }

      // Validate length (2-32 characters)
      if (this.clientId.length < 2 || this.clientId.length > 32) {
        alert(this.$t('client-id-length-error'));
        return;
      }

      // Validate allowed characters: alphanumeric, hyphen, underscore, dot, and forward slash
      if (!/^[a-zA-Z0-9\-_./]+$/.test(this.clientId)) {
        alert(this.$t('client-id-format-error'));
        return;
      }

      if (this.isDefaultTheme) {
        try {
          await generateActivationLink(this.clientId);
          const generatedLink = await getGeneratedLink(this.clientId);
          this.generatedLink = `${generatedLink.data.link}&language=${this.selectedLanguage}`;
        } catch (error) {
          alert(this.$t('link-generation-failed'));
          this.generatedLink = '';
          return;
        }
      } else {
        const host = window.location.origin;
        this.generatedLink = `${host}?clientId=${this.clientId}&language=${this.selectedLanguage}`;
      }
      this.isCopied = false;

      trackEvent('QuestionnaireLinkGenerated_UI', {
        clientId: this.clientId,
        generatedLink: this.generatedLink,
      });
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.generatedLink);
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      } catch (err) {
        // no-op
      }
    },
    async loadLinks() {
      if (this.loadingLinks || this.allLinksLoaded) return;
      this.loadingLinks = true;
      try {
        if (!this.isDefaultTheme) return;
        const professionalId = this.$route.query.professionalId;
        const response = await getLinks({
          professionalId,
          offset: this.offset,
          limit: this.limit,
        });
        const newLinks = response.data || [];
        if (newLinks.length < this.limit) {
          this.allLinksLoaded = true;
        }
        this.links = [...this.links, ...newLinks];
        this.offset += this.limit;
      } catch (error) {
        // no-op
      } finally {
        this.loadingLinks = false;
      }
    },
    handleScroll() {
      const scrollY = window.scrollY || window.pageYOffset;
      const visible = window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;
      if (scrollY + visible >= pageHeight - 200) {
        this.loadLinks();
      }
    },
    async activateLink(link) {
      try {
        await generateActivationLink(link.clientId, link.professionalId);
        link.status = 'active';
      } catch (e) {
        // no-op
      }
    },
    async deactivateLinkHandler(link) {
      try {
        await deactivateLink(link.clientId, link.professionalId);
        link.status = 'inactive';
      } catch (e) {
        // no-op
      }
    },
    async obtainLinkFromList(link) {
      try {
        const response = await getGeneratedLink(
          link.clientId,
          link.professionalId
        );
        this.generatedLink = response.data.link;
        await navigator.clipboard.writeText(this.generatedLink);
        link.justCopied = true;
        setTimeout(() => {
          link.justCopied = false;
        }, 2000);
      } catch (e) {
        // no-op
      }
    },
  },
};
</script>

<style scoped>
.link-generator {
  padding: 20px;
}

.input-group {
  display: flex;
  gap: 10px;
  max-width: 500px;
}

.input-field {
  flex: 1;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
}

.generate-button {
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.generate-button:hover {
  background-color: #45a049;
}

.link-container {
  margin-top: 20px;
}

.link-input-container {
  display: flex;
  gap: 10px;
  width: 100%;
  max-width: 800px;
}

.link-input {
  flex: 1;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  background-color: #f8f8f8;
  min-width: 0;
  word-break: break-all;
}

.copy-button {
  padding: 12px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s;
  min-width: 50px;
}

.copy-button:hover {
  background-color: #45a049;
}

.copy-button.copied {
  background-color: #2196f3;
}

.copy-button.copied:hover {
  background-color: #1976d2;
}
</style>
